import { Logger as AxiomLogger } from 'next-axiom';
import { Transport } from '../types/Transport';
import { LogEntry } from '../types/LogEntry';
import { TransportConfig } from '../types/TransportConfig';
import { LogLevel } from '../types/LogLevel';
import { isDevelopment } from '@shared/utils';

/**
 * @group Classes
 * 
 * @summary
 * <blockquote>Axiom transport for logging to Vercel Axiom service</blockquote>
 * 
 * @example
 * ```typescript
 * const transport = new AxiomTransport({ level: 'info', categories: [] });
 * await transport.log({ timestamp: '2024-01-01T00:00:00Z', level: 'info', category: 'users.signin', message: 'User signed in' });
 * ```
 */
export class AxiomTransport implements Transport {
  private axiomLogger: AxiomLogger;

  constructor(private config: TransportConfig) {
    this.axiomLogger = new AxiomLogger({
      source: 'custom-logger',
    });
  }

  async log(entry: LogEntry): Promise<void> {
    if (!this.shouldLog(entry)) {
      return;
    }

    // Map our log levels to Axiom methods
    const logData = {
      category: entry.category,
      timestamp: entry.timestamp,
      ...entry.context,
      ...(entry.error && { 
        error: {
          message: entry.error.message,
          stack: entry.error.stack,
          name: entry.error.name,
        }
      }),
    };

    // Use Axiom's log methods
    switch (entry.level) {
      case 'debug':
        this.axiomLogger.debug(entry.message, logData);
        break;
      case 'info':
        this.axiomLogger.info(entry.message, logData);
        break;
      case 'warn':
        this.axiomLogger.warn(entry.message, logData);
        break;
      case 'error':
        this.axiomLogger.error(entry.message, logData);
        break;
      default:
        this.axiomLogger.info(entry.message, logData);
    }
  }

  async close(): Promise<void> {
    // Flush any pending logs to Axiom
    await this.axiomLogger.flush();
  }

  private shouldLog(entry: LogEntry): boolean {
    // get environment and if in dev then don't
    const result = isDevelopment();
    const isDev = result.match({
      ok: (dev) => dev,
      err: () => false // Default to false if environment detection fails
    });
    
    if (isDev) {
      return false;
    }

    const levelPriority = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };

    // Only check log level - category/domain filtering is handled at the Logger level
    return levelPriority[entry.level] >= levelPriority[this.config.level];
  }
} 